import {
  Activity,
  BarChart3,
  Boxes,
  Calculator,
  CalendarRange,
  ChartGantt,
  ClipboardList,
  Database,
  Filter,
  FileBarChart,
  FileUp,
  FolderArchive,
  FolderKanban,
  Globe,
  History,
  LayoutDashboard,
  MessageSquare,
  ListChecks,
  ListTree,
  MessageSquarePlus,
  Package,
  PackageCheck,
  PackagePlus,
  PencilRuler,
  Presentation,
  Receipt,
  CalendarOff,
  Plane,
  Settings,
  ShieldAlert,
  ShieldCheck,
  ShoppingCart,
  Sigma,
  SlidersHorizontal,
  Timer,
  TrendingUp,
  Truck,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { Permission } from '@proerp/shared';

/**
 * Sidebar-Abschnitte in Anzeige-Reihenfolge.
 *
 * Gegliedert nach dem Arbeitsablauf eines Projekts bzw. einer Ersatzteil-
 * Bestellung (VB-26015, Struktur vom Melder): erst verkaufen und Kontakte
 * pflegen, dann das Projekt abwickeln, dann Artikel/Bestand, dann Logistik.
 *
 * Die Gruppe heißt bewusst „Projekte" und nicht „Aufträge": „Auftrag" ist im
 * Haus der bestätigte Kundenauftrag (Domänen-Glossar) — der liegt bei den
 * Belegen unter „Verkauf & Kontakte".
 */
export const NAV_GROUPS = [
  'Übersicht',
  'Verkauf & Kontakte',
  // Eigenes Modul (ADR 0112): Räumer-Angebote vor dem Auftrag, getrennt vom Verkauf.
  'Angebotskalkulation',
  'Projekte',
  'Artikel & Lager',
  'Logistik',
  'Technik',
  'Qualität',
  'Montage & Service',
  'Finanzen',
  'Reporting',
  'Personal & Zeit',
  'System',
] as const;
export type NavGroup = (typeof NAV_GROUPS)[number];

/**
 * Alte Gruppennamen → Nachfolger (VB-26015). Die Anzeige-Vorlieben jedes Nutzers
 * (`hiddenNavGroups`, `navGroupOrder`) liegen als KLARTEXT-Namen in der DB. Ohne
 * diese Umschlüsselung würde eine ausgeblendete Gruppe nach der Umbenennung
 * wieder auftauchen und eine sortierte Reihenfolge auf den Standard zurückfallen.
 *
 * „Vertrieb & Projekte" ist auseinandergegangen und hat deshalb ZWEI Nachfolger —
 * wer es ausgeblendet hatte, meinte beide Hälften.
 *
 * „Material" ist zweimal umbenannt worden (erst „Artikel & Lagerverwaltung",
 * dann kurz darauf verkürzt). Beide Zwischenstände bleiben hier stehen: Wer die
 * App zwischendurch offen hatte, hat den mittleren Namen gespeichert, wer nicht,
 * den alten — eine Kette „alt → mittel → neu" gibt es nicht, die Auflösung
 * schlägt genau einmal nach.
 */
export const NAV_GROUP_RENAMES: Record<string, NavGroup[]> = {
  'Vertrieb & Projekte': ['Verkauf & Kontakte', 'Projekte'],
  Referenzen: ['Verkauf & Kontakte'],
  Material: ['Artikel & Lager'],
  'Artikel & Lagerverwaltung': ['Artikel & Lager'],
};

/** Einen gespeicherten Gruppennamen auf die heutigen Gruppen abbilden. */
export function resolveNavGroupNames(saved: string): NavGroup[] {
  if ((NAV_GROUPS as readonly string[]).includes(saved)) return [saved as NavGroup];
  return NAV_GROUP_RENAMES[saved] ?? [];
}

/** Ausgeblendete Gruppen eines Nutzers, alte Namen mitgerechnet. */
export function resolveHiddenNavGroups(saved: string[]): Set<NavGroup> {
  return new Set(saved.flatMap(resolveNavGroupNames));
}

/** URL-Slug eines Bereichs für die Bereichs-Übersicht (`/bereich/:slug`). */
export function navGroupSlug(group: NavGroup): string {
  return group
    .toLowerCase()
    .replace(/&/g, 'und')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Bereich aus einem Slug zurückgewinnen (für die Hub-Route). */
export function navGroupFromSlug(slug: string): NavGroup | undefined {
  return NAV_GROUPS.find((group) => navGroupSlug(group) === slug);
}

/**
 * Vom Nutzer gespeicherte Gruppen-Reihenfolge (`navGroupOrder`) mit der
 * NAV_GROUPS-Standardreihenfolge zusammenführen: bekannte Gruppen behalten
 * die gespeicherte Reihenfolge, fehlende/unbekannte Gruppen (neu, umbenannt)
 * werden dahinter in der Standardreihenfolge angehängt statt zu verschwinden.
 */
export function resolveNavGroupOrder(saved: string[]): NavGroup[] {
  // Umbenannte Gruppen behalten ihren Platz (VB-26015) statt ans Ende zu rutschen;
  // eine aufgeteilte Gruppe bringt ihre Nachfolger direkt hintereinander mit.
  const known: NavGroup[] = [];
  for (const entry of saved) {
    for (const group of resolveNavGroupNames(entry)) {
      if (!known.includes(group)) known.push(group);
    }
  }
  const rest = NAV_GROUPS.filter((group) => !known.includes(group));
  return [...known, ...rest];
}

/**
 * Direkt-Ziel eines Bereichs-Headers statt der generischen Bereichs-Übersicht
 * (`/bereich/:slug`). Für Bereiche mit einer natürlichen Startseite: Ein Klick
 * auf die fette Sektions-Überschrift lädt direkt diese Seite. „Technik" landet
 * auf den Zeichnungsnummern (es gibt keine eigene Übersichtsseite mehr).
 */
export const NAV_GROUP_LANDING: Partial<Record<NavGroup, string>> = {
  Technik: '/engineering',
  // QM ist ein Ein-Punkt-Bereich: Der Header führt direkt auf die QM-Prozesse
  // statt auf eine leere Bereichs-Übersicht.
  Qualität: '/qm',
  Angebotskalkulation: '/angebotskalkulation',
};

export interface NavSubItem {
  label: string;
  to: string;
  /**
   * Unterpunkt nur sichtbar mit dieser Berechtigung (RBAC, ADR 0011). Ein Array
   * ist ODER-verknüpft (z. B. „Personen" für hr.read ODER admin.manage).
   */
  permission?: Permission | Permission[];
  /** Vorübergehend komplett ausgeblendet. */
  hidden?: boolean;
}

export interface NavItem {
  label: string;
  /** Kurzlabel für die mobile Bottom-Tab-Bar (erp-design-guideline.md §5.2). */
  short: string;
  to: string;
  icon: LucideIcon;
  /** Sidebar-Abschnitt, in dem der Punkt erscheint. */
  group: NavGroup;
  /** Unterpunkte — der Punkt wird in der Sidebar zum aufklappbaren Eintrag (sidebar-07). */
  children?: NavSubItem[];
  /** Modul ist fachlich umgesetzt (sonst Platzhalter-Seite). */
  ready?: boolean;
  /**
   * Aktiv-Markierung nur bei exaktem Pfad statt Präfix. Nötig, wenn ein flacher
   * Eintrag auf einem Präfix anderer Einträge liegt (z. B. Zeichnungsnummern auf
   * `/engineering`, während Anträge/Materialcodes unter `/engineering/…` liegen).
   */
  end?: boolean;
  /**
   * Wird nur angezeigt, wenn der Benutzer diese Berechtigung hat (RBAC, ADR 0011).
   * Ein Array ist ODER-verknüpft.
   */
  permission?: Permission | Permission[];
  /** Vorübergehend komplett ausgeblendet (auch für Admins) — Feature noch nicht freigeschaltet. */
  hidden?: boolean;
}

/**
 * Hauptnavigation (CLAUDE.md §1). Verwandte Unterseiten sind zu aufklappbaren
 * Einträgen zusammengefasst, damit die Sidebar übersichtlich bleibt; die
 * eigentlichen Routen bleiben unverändert.
 */
export const NAV_ITEMS: NavItem[] = [
  // ── Übersicht ──
  { label: 'Dashboard', short: 'Start', to: '/', icon: LayoutDashboard, group: 'Übersicht', ready: true },
  { label: 'Analyse', short: 'Analyse', to: '/analyse', icon: BarChart3, group: 'Übersicht', ready: true, permission: 'analytics.read' },
  { label: 'Selektionspool', short: 'Pool', to: '/selektionspool', icon: ListChecks, group: 'Übersicht', ready: true, permission: 'selectionpool.manage' },

  // ── Verkauf & Kontakte ── (VB-26015: Belege, Pipeline, Kontakte und
  // Referenzen gehören zum Verkaufen und stehen deshalb beieinander.)
  { label: 'Verkauf', short: 'Verkauf', to: '/verkauf', icon: Receipt, group: 'Verkauf & Kontakte', ready: true, permission: 'sales.read' },
  { label: 'Vertriebspipeline', short: 'Pipeline', to: '/vertriebspipeline', icon: Filter, group: 'Verkauf & Kontakte', ready: true, permission: 'sales.read' },
  {
    label: 'CRM',
    short: 'CRM',
    to: '/crm',
    icon: Users,
    group: 'Verkauf & Kontakte',
    ready: true,
    permission: 'crm.read',
    children: [
      { label: 'Kunden & Lieferanten', to: '/crm' },
      { label: 'Ansprechpartner', to: '/crm/ansprechpartner' },
      { label: 'Lieferadressen', to: '/crm/lieferadressen' },
      { label: 'Ausdrucke / Berichte', to: '/crm/berichte' },
    ],
  },
  // Referenzen: seit VB-26015 kein eigener Bereich mehr — sie sind das
  // Verkaufsargument und stehen bei den Kontakten. Karte als zweite Ansicht (ADR 0063).
  {
    label: 'Referenzen',
    short: 'Referenzen',
    to: '/referenzen',
    icon: Globe,
    group: 'Verkauf & Kontakte',
    ready: true,
    // ADR 0101: Referenzen sind eine eigene Entität mit eigenem Berechtigungspaar.
    // Sichtbarkeit ändert sich für bestehende Rollen nicht — der Rollen-Seeder gibt
    // jeder Rolle mit `projects.read` automatisch auch `references.read`.
    permission: 'references.read',
    children: [
      { label: 'Referenzliste', to: '/referenzen' },
      { label: 'Karte', to: '/referenzen/karte' },
    ],
  },

  // ── Angebotskalkulation ── (ADR 0112) Eigener Bereich, nur mit `quotecalc.read`
  // sichtbar — vergeben über die Zusatzrolle „Angebotskalkulation".
  { label: 'Kalkulationen', short: 'Kalkulation', to: '/angebotskalkulation', icon: Sigma, group: 'Angebotskalkulation', ready: true, permission: 'quotecalc.read', end: true },
  { label: 'Spezifikationen', short: 'Merkmale', to: '/angebotskalkulation/spezifikationen', icon: ListChecks, group: 'Angebotskalkulation', ready: true, permission: 'quotecalc.read' },
  { label: 'Formeln & Vorlagen', short: 'Vorlagen', to: '/angebotskalkulation/vorlagen', icon: SlidersHorizontal, group: 'Angebotskalkulation', ready: true, permission: 'quotecalc.settings' },

  // ── Projekte ── (VB-26015: die Abwicklung bekommt einen eigenen Bereich.)
  { label: 'Projekte', short: 'Projekte', to: '/projekte', icon: FolderKanban, group: 'Projekte', ready: true, permission: 'projects.read' },
  { label: 'Zeitplan', short: 'Zeitplan', to: '/zeitplan', icon: ChartGantt, group: 'Projekte', ready: true, permission: 'projects.read' },
  {
    label: 'Teambesprechung',
    short: 'Team',
    to: '/projekte/teambesprechung',
    icon: Presentation,
    group: 'Projekte',
    ready: true,
    permission: 'projects.read',
  },

  // ── Technik ── (Die Gruppen-Überschrift „Technik" springt auf die Zeichnungsnummern,
  // siehe NAV_GROUP_LANDING. Darunter die vier Seiten flach — kein aufklappbarer Eintrag.)
  // VB-26015: die vier Nummern-Seiten liegen unter einem aufklappbaren Punkt
  // „Artikelverwaltung" — die Sidebar kennt genau diese zweite Ebene.
  {
    label: 'Artikelverwaltung',
    short: 'Artikelverw.',
    to: '/engineering',
    icon: PencilRuler,
    group: 'Technik',
    ready: true,
    permission: 'engineering.read',
    children: [
      { label: 'Zeichnungsnummern', to: '/engineering' },
      { label: 'Anträge', to: '/engineering/antraege' },
      { label: 'Materialcodes', to: '/engineering/materialcodes' },
      { label: 'Warengruppen', to: '/engineering/warengruppen' },
    ],
  },
  // Zusatzmaterial am Artikel (VB-26016, ADR 0088). Der Melder nannte den Punkt
  // „Baugruppenverwaltung" — der Name ist im Haus aber schon vergeben: In
  // Bedarfsliste und Kommissionierung ist eine „Baugruppe" eine Gruppierung von
  // Positionen. Zwei Dinge gleich zu benennen wäre teuer (§12), deshalb heißt
  // der Punkt wie die Sache selbst.
  {
    label: 'Zusatzmaterial',
    short: 'Zusatzmat.',
    to: '/engineering/zusatzmaterial',
    icon: PackagePlus,
    group: 'Technik',
    ready: true,
    permission: 'inventory.read',
  },
  // Stücklisten je Projekt (VB-26025, ADR 0091). Eigener Punkt neben der
  // Artikelverwaltung: dort liegen die Nummern-Stammdaten, hier die Liste, die
  // für ein konkretes Projekt konstruiert wird.
  {
    label: 'Stücklisten',
    short: 'Stückl.',
    to: '/engineering/stuecklisten',
    icon: ListTree,
    group: 'Technik',
    ready: true,
    permission: 'engineering.read',
  },
  // Der Katalog hinter „Aus Vorlage füllen" (ADR 0092) — Stammdaten, deshalb ein
  // eigener Punkt neben den Stücklisten selbst.
  {
    label: 'Stücklisten-Vorlagen',
    short: 'Vorlagen',
    to: '/engineering/stuecklisten-vorlagen',
    icon: ClipboardList,
    group: 'Technik',
    ready: true,
    permission: 'engineering.read',
  },

  // ── Qualität ── (Eigenständiges Modul, nicht mehr unter „Technik" — QM betrifft alle Bereiche.
  // Der Bereichs-Header springt direkt auf die QM-Prozesse, siehe NAV_GROUP_LANDING.)
  { label: 'QM-Prozesse', short: 'QM', to: '/qm', icon: ShieldAlert, group: 'Qualität', ready: true, permission: 'qm.read' },

  // ── Artikel & Lager ── (vormals „Material", kurz auch „Artikel &
  // Lagerverwaltung"; VB-26015. Artikelstamm, Bestand, Bedarf und Einkauf.
  // Bewusst NICHT „Fertigung" — wir fertigen nicht selbst, s. Domänen-Reframe.
  // Der Bereich heißt bewusst wie sein erster Eintrag: „…verwaltung" sagt
  // nichts dazu und war in der Seitenleiste die längste Überschrift.)
  {
    label: 'Artikel & Lager',
    short: 'Lager',
    to: '/einkauf',
    icon: Package,
    group: 'Artikel & Lager',
    ready: true,
    permission: 'inventory.read',
    children: [
      // Lagercockpit (2026-09-07): Dispositions-Sicht des Einkaufs — was heute
      // bestellt werden muss, was reinkommt, was knapp wird. Steht vor dem
      // Artikelstamm, weil der Tag im Lager damit beginnt.
      { label: 'Lagercockpit', to: '/lagercockpit' },
      { label: 'Artikel', to: '/einkauf' },
      { label: 'Artikelbewegungen', to: '/lager-bewegungen' },
      { label: 'Inventur', to: '/einkauf/inventur' },
      // Analyse-Seite (Lagerwert je Warengruppe, Sortiment) — bis 2026-09-07 nur
      // per Knopf erreichbar; Cockpit = Handeln, Analyse = Verstehen.
      { label: 'Analyse', to: '/einkauf/analyse' },
    ],
  },
  // Materialbedarf steht vor der Beschaffung: erst steht fest, WAS gebraucht wird,
  // dann wird bestellt. Recht bleibt `projects.read` — die Positionen gehören
  // zwingend zu einem Projekt (Tech-Lead 2026-07-30, vormals Projekt-Unter-Tab).
  { label: 'Materialbedarf', short: 'Bedarf', to: '/materialbedarf', icon: ClipboardList, group: 'Artikel & Lager', ready: true, permission: 'projects.read' },
  { label: 'Bedarfsvorschau', short: 'Vorschau', to: '/bedarfsvorschau', icon: TrendingUp, group: 'Artikel & Lager', ready: true, permission: 'forecast.read' },
  { label: 'Beschaffung', short: 'Beschaffung', to: '/beschaffung', icon: ShoppingCart, group: 'Artikel & Lager', ready: true, permission: 'procurement.read' },
  // Sperrlager (VB-26015): Restmaterial ohne Stammdaten-Bezug ist Bestandsführung,
  // keine Logistik — steht deshalb beim Lager statt beim Warenausgang.
  { label: 'Sperrlager', short: 'Sperrlager', to: '/sperrlager', icon: Boxes, group: 'Artikel & Lager', ready: true, permission: 'blockedstock.read' },

  // ── Logistik ── (Warenbewegung. Reihenfolge nach dem Alltag der Abwicklung
  // (VB-26015): erst kommissionieren, dann Wareneingang, dann Transporte. Bis
  // dahin stand der Wareneingang vorn — „der Weg der Ware beginnt an der Rampe",
  // ADR 0072; der Melder arbeitet umgekehrt von der Auslieferung her.)
  { label: 'Kommissionierung', short: 'Kommiss.', to: '/kommissionierung', icon: ClipboardList, group: 'Logistik', ready: true, permission: 'commissioning.read' },
  { label: 'Wareneingang', short: 'Eingang', to: '/wareneingang', icon: PackageCheck, group: 'Logistik', ready: true, permission: 'goodsreceipt.read' },
  // Transporte steht bewusst eigenständig neben der Kommissionierung: ein ausgehender
  // Transport bündelt Collis aus MEHREREN Kommissionieraufträgen (Sammelladung, ADR 0066);
  // eingehende Transporte (Wareneingang-Logistik) kamen mit ADR 0071 dazu.
  { label: 'Transporte', short: 'Transporte', to: '/transporte', icon: Truck, group: 'Logistik', ready: true, permission: 'transport.read' },

  // ── Montage & Service ── (Vor-Ort-Montage & Wartung beim Kunden.)
  { label: 'Montage & Wartung', short: 'Montage', to: '/montage', icon: Wrench, group: 'Montage & Service', ready: true, permission: 'assembly.read' },

  // ── Personal & Zeit ── (HR liegt jetzt unter „Administration", siehe unten)
  {
    // Dashboard + Zeitprotokoll liegen zusammen auf einer Seite (keine Untergruppen) —
    // Klick führt direkt aufs Zeiterfassungs-Dashboard.
    label: 'Zeiterfassung',
    short: 'Zeit',
    to: '/zeiterfassung',
    icon: Timer,
    group: 'Personal & Zeit',
    ready: true,
    permission: 'timetracking.read',
  },
  {
    // Self-Service-Analog zur Admin-„Zeit-Analyse" unten — nur die eigenen
    // Buchungen eines Kalenderjahres, für jeden mit timetracking.read sichtbar.
    label: 'Jahresübersicht',
    short: 'Jahr',
    to: '/zeiterfassung-jahresuebersicht',
    icon: CalendarRange,
    group: 'Personal & Zeit',
    ready: true,
    permission: 'timetracking.read',
  },
  {
    // Admin-Auswertung aller Mitarbeiter — nur mit timetracking.manage sichtbar.
    label: 'Zeit-Analyse',
    short: 'Analyse',
    to: '/zeiterfassung-analyse',
    icon: BarChart3,
    group: 'Personal & Zeit',
    ready: true,
    permission: 'timetracking.manage',
  },
  {
    label: 'Zeit-Stammdaten',
    short: 'Stammd.',
    to: '/zeiterfassung-stammdaten',
    icon: Database,
    group: 'Personal & Zeit',
    ready: true,
    permission: 'timetracking.manage',
  },
  {
    // Import der Zeiteinträge aus der Alt-App (Trockenlauf) — nur Admins.
    label: 'Zeit-Import',
    short: 'Import',
    to: '/zeiterfassung-import',
    icon: FileUp,
    group: 'Personal & Zeit',
    ready: true,
    permission: 'timetracking.manage',
  },
  {
    label: 'FZ',
    short: 'FZ',
    to: '/fehlzeiten',
    icon: CalendarOff,
    group: 'Personal & Zeit',
    ready: true,
    permission: 'leave.read',
    children: [
      { label: 'Anträge', to: '/fehlzeiten' },
      { label: 'Urlaubskalender', to: '/urlaubskalender' },
    ],
  },
  {
    label: 'Reisekosten',
    short: 'Reisen',
    to: '/reisekosten',
    icon: Plane,
    group: 'Personal & Zeit',
    ready: true,
    permission: 'travel.read',
  },

  // ── Finanzen ──
  { label: 'Buchhaltung', short: 'Buchhaltung', to: '/buchhaltung', icon: Calculator, group: 'Finanzen', ready: true, permission: 'accounting.read' },

  // ── Reporting ── (Stakeholder + Buchhaltung: Auftragseingang, Umsatz, Lagerwert, HF/NAL)
  { label: 'Reporting', short: 'Reporting', to: '/reporting', icon: FileBarChart, group: 'Reporting', ready: true, permission: 'reporting.read' },

  // ── System ──
  { label: 'App-Cockpit', short: 'Cockpit', to: '/cockpit', icon: Activity, group: 'System', ready: true, permission: 'admin.manage' },
  // DMS vorerst ausgeblendet (Tech-Lead 2026-07-03) — Route bleibt bestehen, nur aus der Navigation entfernt.
  { label: 'Dateien', short: 'Dateien', to: '/dateien', icon: FolderArchive, group: 'System', ready: true, permission: 'dms.read', hidden: true },
  { label: 'Einstellungen', short: 'Einstellungen', to: '/einstellungen', icon: Settings, group: 'System', ready: true, permission: 'settings.read' },
  {
    label: 'Administration',
    short: 'Admin',
    to: '/administration',
    icon: ShieldCheck,
    group: 'System',
    ready: true,
    children: [
      // Personen bündelt Mitarbeiter-Stammdaten (HR) und Login/Rollen (vormals
      // getrennte Seiten „HR" + „Benutzer & Rechte" → siehe HrPage.tsx).
      { label: 'Personen', to: '/hr', permission: ['hr.read', 'admin.manage'] },
      { label: 'Rollen', to: '/administration', permission: 'admin.manage' },
      // Zugänge von AUSSEN (ADR 0085) — bewusst neben, nicht in „Personen":
      // Partner sind keine Mitarbeitenden.
      { label: 'Partnerzugänge', to: '/partnerzugaenge', permission: 'admin.manage' },
      { label: 'Import', to: '/import', permission: 'migration.run' },
      { label: 'Base44-Import', to: '/base44-import', permission: 'migration.run' },
      { label: 'Referenzen-Import', to: '/referenzen-import', permission: 'migration.run' },
      { label: 'Datenbank', to: '/datenbank', permission: 'data.write' },
      { label: 'Audit-Log', to: '/audit-log', permission: 'audit.read' },
    ],
  },
  // Verbesserungen (ADR 0078): Basisrecht `feedback.read` hat jede Rolle — der
  // Punkt ist also für alle sichtbar, die Liste zeigt aber ohne `feedback.manage`
  // nur die eigenen Meldungen (serverseitig gefiltert).
  // Teamchat (ADR 0064): Backend + Seite waren fertig, es fehlte nur der
  // Navigationseintrag — die Route war über die Oberfläche nicht erreichbar
  // (Navigations-Audit 2026-09-10, Befund #6). `/v1/chat/unread-count` liegt
  // bereits als „Nav-Badge" gedacht bereit; ein Ungelesen-Abzeichen an diesem
  // Punkt ist als eigener Schritt offen.
  { label: 'Teamchat', short: 'Chat', to: '/chat', icon: MessageSquare, group: 'System', ready: true, permission: 'chat.read' },
  {
    label: 'Verbesserungen',
    short: 'Feedback',
    to: '/verbesserungen',
    icon: MessageSquarePlus,
    group: 'System',
    ready: true,
    permission: 'feedback.read',
  },
  { label: 'Update-Log', short: 'Updates', to: '/changelog', icon: History, group: 'System', ready: true },
];
